<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { DocumentsApi } from '../services/documentsApi'
import type { DocumentAudioKind, DocumentAudioManifest } from '../types/document.types'
import { mergeAudioManifest, needsAudioRefresh } from '../utils/audioPlayback'

const props = defineProps<{ documentId: string }>()
const manifest = ref<DocumentAudioManifest | null>(null)
const active = ref<DocumentAudioKind | null>(null)
const player = ref<HTMLAudioElement | null>(null)
const busy = ref(false)
const message = ref('')
const shouldPlay = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined
let revision = 0
let disposed = false
const selected = computed(() => manifest.value?.tracks.find(t => t.kind === active.value))
const url = computed(() => selected.value?.file?.url || '')
const title = computed(() => active.value === 'summary' ? 'Краткая версия' : 'Полная версия')
const labels = { summary: 'Слушать краткую версию', full: 'Слушать полную версию' } as const
const kinds: DocumentAudioKind[] = ['summary', 'full']

function stop() {
  player.value?.pause()
  shouldPlay.value = false
  if (timer) clearTimeout(timer)
}

function close() {
  stop()
  revision++
  busy.value = false
  active.value = null
  message.value = ''
}

function schedule() {
  if (timer) clearTimeout(timer)
  if (needsAudioRefresh(manifest.value))
    timer = setTimeout(() => load(), active.value ? 5000 : 10000)
}

async function load() {
  const current = ++revision
  try {
    const data = await DocumentsApi.fetchAudio(props.documentId)
    if (disposed || current !== revision) return
    if (manifest.value?.version_id && manifest.value.version_id !== data.version_id) close()
    // A refreshed presigned URL must never restart the recording already being heard.
    for (const track of data.tracks) {
      const previous = manifest.value?.tracks.find(t => t.kind === track.kind)
      if (previous?.generation && previous.generation !== track.generation && active.value === track.kind) close()
    }
    manifest.value = mergeAudioManifest(manifest.value, data)
    message.value = ''
    schedule()
  } catch {
    if (!disposed && current === revision) {
      stop()
      message.value = 'Не удалось загрузить аудио. Попробуйте ещё раз.'
    }
  }
}

async function select(kind: DocumentAudioKind) {
  stop()
  const current = ++revision
  active.value = kind
  busy.value = true
  message.value = ''
  try {
    const data = await DocumentsApi.prepareAudio(props.documentId, kind)
    if (disposed || current !== revision) return
    manifest.value = data
    shouldPlay.value = true
    // Refresh private S3 links before starting playback.
    await load()
    await nextTick()
    await playWhenReady()
  } catch {
    if (!disposed && current === revision) message.value = 'Не удалось подготовить аудио. Попробуйте ещё раз.'
  } finally {
    if (!disposed && active.value === kind) busy.value = false
  }
}

async function playWhenReady() {
  if (!shouldPlay.value) return
  try {
    await player.value?.play()
  } catch {
    message.value = 'Аудио готово. Нажмите кнопку воспроизведения.'
  }
}

watch(url, async (value, previous) => {
  if (!value || value === previous) return
  await nextTick()
  await playWhenReady()
})

onMounted(load)
onBeforeUnmount(() => {
  disposed = true
  revision++
  stop()
  if (player.value) {
    player.value.removeAttribute('src')
    player.value.load()
  }
})
</script>

<template>
  <div class="document-audio" @click.stop @keydown.stop>
    <div class="d-flex align-center gap-1">
      <span class="text-caption text-medium-emphasis me-1">Аудиоверсии</span>
      <VTooltip v-for="kind in kinds" :key="kind" :text="labels[kind]">
        <template #activator="{ props: tooltip }">
          <span v-bind="tooltip">
            <VBtn
              :icon="kind === 'summary' ? 'ri-file-list-3-line' : 'ri-headphone-line'"
              :aria-label="labels[kind]"
              :color="active === kind ? 'primary' : undefined"
              :variant="active === kind ? 'tonal' : 'text'"
              size="small"
              :loading="busy && active === kind"
              :disabled="busy || !manifest || manifest.tracks.find(t => t.kind === kind)?.status === 'unavailable'"
              @click="select(kind)"
            />
          </span>
        </template>
      </VTooltip>
      <span v-if="manifest?.tracks.every(t => t.status === 'unavailable')" class="text-caption text-medium-emphasis">
        Нет распознанного текста
      </span>
      <VBtn v-if="!manifest && message" variant="text" size="small" @click="load">Повторить</VBtn>
    </div>
    <div v-if="active" class="document-audio__player mt-1 pa-3">
      <div class="d-flex align-center justify-space-between gap-2">
        <span class="text-caption font-weight-medium">{{ title }}</span>
        <VBtn icon="ri-close-line" variant="text" size="x-small" aria-label="Закрыть аудиоплеер" @click="close" />
      </div>
      <audio
        v-if="url"
        :key="active"
        ref="player"
        :src="url"
        controls
        preload="metadata"
        :aria-label="title"
        @ended="shouldPlay = false"
        @play="shouldPlay = true"
        @error="message = 'Не удалось воспроизвести аудио. Нажмите иконку версии для повторной загрузки.'"
      />
      <p v-if="selected && ['waiting', 'queued', 'generating'].includes(selected.status)" class="text-caption text-medium-emphasis mt-1 mb-0" role="status">
        {{ selected.status === 'waiting' ? 'Завершается подготовка текста и краткой версии…' : selected.reason === 'speech_wait' ? 'Ожидаем доступности сервиса озвучивания…' : 'Готовим аудиозапись целиком…' }}
      </p>
      <p v-if="selected?.status === 'failed'" class="text-caption text-error mt-1 mb-0" role="alert">
        Озвучивание не завершилось. Нажмите иконку версии, чтобы повторить.
      </p>
      <p v-if="message" class="text-caption mt-1 mb-0" role="status">{{ message }}</p>
    </div>
  </div>
</template>

<style scoped>
.document-audio__player { border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); border-radius: 8px; background: rgb(var(--v-theme-surface)); }
.document-audio audio { display: block; inline-size: 100%; block-size: 36px; margin-block: 6px; }
</style>
